import React, { useState } from 'react'
import axios from 'axios'
import Modal from '../../shared/modal'

export interface IssuedVoucher {
  id: number
  token: string
  status: string
  expires_at: string | null
}

interface Props {
  queuedOrderId: number
  mealName: string | null
  orderId: number | null
  orderNumber: string | null
  customerPhone: string | null
  defaultValidityHours: number | null
  onClose: () => void
  onIssued: (voucher: IssuedVoucher) => void
}

const PRESET_HOURS = [24, 72, 168, 336]
const FALLBACK_HOURS = 168

const CUSTOM = 'custom'

type Validity = number | typeof CUSTOM

// A voucher that expires the moment it is issued compensates nobody, so the date picker
// starts at tomorrow.
const isoDaysFromNow = (days: number) => {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date.toISOString().slice(0, 10)
}

// Whole days read better than the hours the setting is stored in — "7 days", not "168h".
const durationLabel = (hours: number) => {
  if (hours % 24 !== 0) return hours === 1 ? '1 hour' : `${hours} hours`

  const days = hours / 24
  return days === 1 ? '1 day' : `${days} days`
}

// One button per duration: the presets, plus the location's configured window when it isn't
// already one of them.
const validityHourOptions = (defaultHours: number | null) => {
  if (!defaultHours || PRESET_HOURS.includes(defaultHours)) return PRESET_HOURS

  return [...PRESET_HOURS, defaultHours].sort((a, b) => a - b)
}

const IssueMealVoucherModal: React.FC<Props> = ({
  queuedOrderId,
  mealName,
  orderId,
  orderNumber,
  customerPhone,
  defaultValidityHours,
  onClose,
  onIssued
}) => {
  const [note, setNote] = useState('')
  // No phone number on the order means there is nowhere to text the link, so the checkbox
  // starts off — a disabled control must never submit send_sms: true.
  const [sendSms, setSendSms] = useState(!!customerPhone)
  // Preselects the location's own validity window, so leaving the form alone grants what
  // an automatically-issued voucher would get.
  const [validity, setValidity] = useState<Validity>(defaultValidityHours || FALLBACK_HOURS)
  const [customDate, setCustomDate] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const tomorrow = isoDaysFromNow(1)

  const options: { key: string | number; label: string; value: Validity }[] = [
    ...validityHourOptions(defaultValidityHours).map((hours) => ({
      key: hours,
      label: durationLabel(hours),
      value: hours as Validity
    })),
    { key: CUSTOM, label: 'Custom', value: CUSTOM }
  ]

  const submit = () => {
    if (validity === CUSTOM && customDate === '') {
      setError('Please pick a custom expiry date.')
      return
    }
    if (validity === CUSTOM && customDate < tomorrow) {
      setError('The expiry date has to be in the future.')
      return
    }

    const payload: Record<string, string | number | boolean> = { note, send_sms: sendSms }
    if (validity === CUSTOM) payload.expires_at = customDate
    if (typeof validity === 'number') payload.validity_hours = validity

    setSubmitting(true)
    setError(null)
    axios
      .post(`/queued_orders/${queuedOrderId}/issue_voucher`, payload)
      .then(({ data }) => onIssued(data.voucher))
      .catch((err) => {
        setError(err.response?.data?.error || 'Could not issue the voucher.')
        setSubmitting(false)
      })
  }

  const footer = (
    <>
      <button type='button' className='btn btn-secondary' onClick={onClose} disabled={submitting}>
        Cancel
      </button>
      <button type='button' className='btn btn-primary' onClick={submit} disabled={submitting}>
        {submitting ? 'Issuing…' : 'Issue voucher'}
      </button>
    </>
  )

  return (
    <Modal headerText='Issue voucher for this meal' onClose={onClose} footerContent={footer} centered>
      {error && <div className='alert alert-danger'>{error}</div>}

      <p className='text-muted small'>
        Worth one free meal, tied to this meal so it shows up on the order and in the voucher list.
      </p>

      <dl className='row mb-3'>
        <dt className='col-5'>Meal</dt>
        <dd className='col-7'>
          #{queuedOrderId}
          {mealName ? ` — ${mealName}` : ''}
        </dd>
        <dt className='col-5'>Order</dt>
        <dd className='col-7'>
          {orderId
            ? <a href={`/orders/${orderId}`}>#{orderNumber || orderId}</a>
            : <span className='text-muted'>none</span>}
        </dd>
        <dt className='col-5'>Customer</dt>
        <dd className='col-7'>{customerPhone || <span className='text-muted'>no phone number</span>}</dd>
      </dl>

      <div className='form-group mb-3'>
        <label>Validity</label>
        <div className='voucher-validity-presets' style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {options.map((option) => {
            const selected = validity === option.value
            return (
              <button
                type='button'
                key={option.key}
                aria-pressed={selected}
                className={`btn ${selected ? 'btn-primary active' : 'btn-outline-secondary'}`}
                onClick={() => setValidity(option.value)}
              >
                {option.label}
              </button>
            )
          })}
        </div>
        {validity === CUSTOM && (
          <input
            type='date'
            className='form-control mt-2'
            value={customDate}
            min={tomorrow}
            onChange={(e) => setCustomDate(e.target.value)}
          />
        )}
      </div>

      <div className='form-group mb-3'>
        <label htmlFor='meal-voucher-note'>Note (optional)</label>
        <input
          id='meal-voucher-note'
          type='text'
          className='form-control'
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder='Why is this voucher being granted?'
        />
      </div>

      <div className='form-check'>
        <input
          id='meal-voucher-send-sms'
          type='checkbox'
          className='form-check-input'
          checked={sendSms}
          onChange={(e) => setSendSms(e.target.checked)}
          disabled={!customerPhone}
        />
        <label className='form-check-label' htmlFor='meal-voucher-send-sms'>
          Text the voucher link to the customer now
        </label>
      </div>
    </Modal>
  )
}

export default IssueMealVoucherModal
